/**
 * 业务日（business day）—— web 端「今天 / 昨天 / 近 N 天 / 某一天」的唯一实现。
 * 与 main-mobile `src/utils/businessDay.ts` 同口径（2026-10-31 业务日时区整改）：
 *
 * - 业务日 = **用户时区**（user.timezone）下的日历日；不看浏览器时区，也不看 UTC。
 * - 用户时区为空 / 不认识 → 平台回落时区 America/New_York（与后端 sys.fallback_timezone 同值）。
 * - 发给后端的「按天」条件只发**日期**（dayFilter / dayRangeFilter），由后端按用户时区（任务按物业时区）切天；
 *   不要在前端算「今天 0 点」再转成 UTC 发过去——带 Z 的时刻后端原样比较，不再切天。
 *   约定见 docs/SEARCH_AND_LIST_PARAMETER_GUIDE.md「时区上下文」。
 *
 * 所有函数都是纯函数，时区显式传入；ESLint 规则拦着别处再手写 UTC 日界。
 */
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'

/** 用户时区为空 / 不认识时的回落（= 后端 sys.fallback_timezone 的默认值）。 */
export const BUSINESS_TZ_FALLBACK = 'America/New_York'

/** 'YYYY-MM-DD'：业务时区下的一个日历日。 */
export type DayKey = string

export interface DslDateRange {
  type: 'date'
  operator: 'between'
  value: [DayKey, DayKey]
}

/** 合法的 IANA 时区原样返回，否则回落 BUSINESS_TZ_FALLBACK（绝不回落到浏览器时区或 UTC）。 */
export function resolveBusinessTimezone(tz?: string | null): string {
  if (!tz) return BUSINESS_TZ_FALLBACK
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return tz
  } catch {
    return BUSINESS_TZ_FALLBACK
  }
}

/** 时刻 `instant` 落在业务时区的哪一天。 */
export function dayKeyOf(instant: string | Date, tz?: string | null): DayKey {
  return formatInTimeZone(instant, resolveBusinessTimezone(tz), 'yyyy-MM-dd')
}

/** 业务时区的今天。 */
export function todayKey(tz?: string | null, now: Date = new Date()): DayKey {
  return dayKeyOf(now, tz)
}

const utcAnchor = (key: DayKey): Date => {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}
const keyOfAnchor = (d: Date): DayKey =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`

/** 日历日加减：纯日期运算，不碰时区和夏令时。 */
export function addDays(key: DayKey, n: number): DayKey {
  const d = utcAnchor(key)
  d.setUTCDate(d.getUTCDate() + n)
  return keyOfAnchor(d)
}

/** 日历月加减；目标月没有这一天时落到月末（10-31 减 1 个月 = 09-30）。 */
export function addMonths(key: DayKey, n: number): DayKey {
  const [y, m, d] = key.split('-').map(Number)
  const lastDay = new Date(Date.UTC(y, m + n, 0)).getUTCDate()
  return keyOfAnchor(new Date(Date.UTC(y, m - 1 + n, Math.min(d, lastDay))))
}

/** 从 `from` 到 `to` 相差几天（to 更晚为正）。 */
export function diffDays(from: DayKey, to: DayKey): number {
  return Math.round((utcAnchor(to).getTime() - utcAnchor(from).getTime()) / 86_400_000)
}

/**
 * 业务时区里这一天的 [0 点, 次日 0 点)，换成 UTC ISO。
 * 只给「真要发时刻」的地方用；按天的过滤请用 dayFilter。
 */
export function dayRangeUtc(key: DayKey, tz?: string | null): { startIso: string; endIso: string } {
  const zone = resolveBusinessTimezone(tz)
  return {
    startIso: fromZonedTime(`${key}T00:00:00`, zone).toISOString(),
    endIso: fromZonedTime(`${addDays(key, 1)}T00:00:00`, zone).toISOString(),
  }
}

/** DSL：某一天（后端按时区上下文切天）。 */
export function dayFilter(key: DayKey): DslDateRange {
  return dayRangeFilter(key, key)
}

/** DSL：从 `from` 到 `to` 这几天，两头都含（后端按时区上下文切天）。 */
export function dayRangeFilter(from: DayKey, to: DayKey): DslDateRange {
  return { type: 'date', operator: 'between', value: [from, to] }
}
